// frontend/src/components/ProjectDetail/panels/JournalPanel.js

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Plus, List, Calendar as CalendarIcon, Search, Loader2, ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import JournalEntryForm from './JournalEntryForm';
import JournalEntryListItem from './JournalEntryListItem';
import { apiService } from '../../../services/api';

const daysOfWeek = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

const normalizeDateUTC = (dateInput) => {
  const d = new Date(dateInput);
  return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
};

const getSafeTimestamp = (value) => {
  if (!value) return 0;
  const ts = new Date(value).getTime();
  return Number.isNaN(ts) ? 0 : ts;
};

const getEntrySortTimestamp = (entry) => {
  if (!entry) return 0;
  if (entry.entry_date) {
    const entryTs = getSafeTimestamp(entry.entry_date);
    if (entryTs) {
      return entryTs;
    }
  }
  return getSafeTimestamp(entry?.created_at);
};


const JournalPanel = ({
  projectId,
  project,
  journalEntries = [],
  onAddEntry,
  onUpdateEntry,
  onDeleteEntry,
  onFormClose, // ✅ NOUVELLE PROP
  employees = []
}) => {
  const [viewMode, setViewMode] = useState('list');
  const [showFormOrEditingEntry, setShowFormOrEditingEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const trimmedSearchTerm = searchTerm.trim();
  const normalizedSearchTerm = trimmedSearchTerm.toLowerCase();
  const hasSearchTerm = normalizedSearchTerm.length > 0;

  const [currentMonthDate, setCurrentMonthDate] = useState(new Date());
  const [selectedAgendaDate, setSelectedAgendaDate] = useState(new Date());

  const [hoveredDay, setHoveredDay] = useState(null);

  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const getUsernameById = useCallback((userId) => {
    const user = employees.find(emp => emp.id === userId || emp.user_id === userId);
    return user ? user.name : 'Utilisateur inconnu';
  }, [employees]);

  const entryMatchesSearch = useCallback((entry) => {
    if (!normalizedSearchTerm) return true;
    const title = entry.title || '';
    const content = entry.content || '';
    const tags = Array.isArray(entry.tags) ? entry.tags : [];
    return (
      title.toLowerCase().includes(normalizedSearchTerm) ||
      content.toLowerCase().includes(normalizedSearchTerm) ||
      tags.some((tag) => (tag || '').toLowerCase().includes(normalizedSearchTerm))
    );
  }, [normalizedSearchTerm]);

  const filteredEntriesForList = useMemo(() => {
    return journalEntries
      .filter(entryMatchesSearch)
      .sort((a, b) => {
        const diff = getEntrySortTimestamp(b) - getEntrySortTimestamp(a);
        if (diff !== 0) return diff;
        return getSafeTimestamp(b.created_at) - getSafeTimestamp(a.created_at);
      });
  }, [journalEntries, entryMatchesSearch]);

  const entriesForSelectedAgendaDate = useMemo(() => {
    if (viewMode !== 'agenda') return [];
    const normalizedSelected = normalizeDateUTC(selectedAgendaDate);

    return journalEntries
      .filter(entry => {
        const normalizedEntryDate = normalizeDateUTC(entry.entry_date);
        return normalizedEntryDate.getTime() === normalizedSelected.getTime();
      })
      .filter(entryMatchesSearch)
      .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
  }, [journalEntries, selectedAgendaDate, viewMode, entryMatchesSearch]);


  const handleFormSubmit = async (entryData, entryId = null) => {
    setIsSaving(true);
    try {
      let savedEntry;
      const isNewEntry = !entryId || typeof entryId !== 'number';

      if (isNewEntry) {
        savedEntry = await onAddEntry(entryData);
      } else {
        savedEntry = await onUpdateEntry(entryId, entryData);
      }

      if (isNewEntry && savedEntry && savedEntry.title.startsWith("Entrée du")) {
        const suggestion = await apiService.suggestJournalTitle(projectId, savedEntry.content);
        if (suggestion && suggestion.suggested_title) {
          await onUpdateEntry(savedEntry.id, { title: suggestion.suggested_title });
        }
      }

      if (isNewEntry) {
        setShowFormOrEditingEntry(savedEntry);
      }

      return savedEntry;
    } catch (error) {
      console.error("Erreur sauvegarde Journal:", error);
      return null;
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = useCallback((entry) => {
    setShowFormOrEditingEntry(entry);
  }, []);

  const handleDelete = async (entryId) => {
    if (window.confirm("Êtes-vous sûr de vouloir supprimer cette entrée ?")) {
      setDeletingId(entryId);
      try {
        await onDeleteEntry(entryId);
      } catch (error) {
        console.error("Erreur suppression Journal:", error);
      } finally {
        setDeletingId(null);
      }
    }
  };

  const handleAddNewEntryForDate = (date) => {
    const newEntryTemplate = {
      id: `new-${Date.now()}`,
      entry_date: date,
      title: '',
      content: '',
    };
    setShowFormOrEditingEntry(newEntryTemplate);
  };

  const handleCloseForm = () => {
    if (onFormClose) {
      onFormClose(); // ✅ Rafraîchir les données
    }
    setShowFormOrEditingEntry(null); // Fermer le formulaire
  };

  const changeMonth = (offset) => {
    setCurrentMonthDate(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(newDate.getMonth() + offset);
      return newDate;
    });
  };

  const getDaysInMonthForAgenda = () => {
    const year = currentMonthDate.getFullYear();
    const month = currentMonthDate.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);

    const days = [];
    let startDayOfWeek = firstDayOfMonth.getDay();
    startDayOfWeek = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

    for (let i = 0; i < startDayOfWeek; i++) {
      const date = new Date(firstDayOfMonth);
      date.setDate(date.getDate() - (startDayOfWeek - i));
      days.push({ date: normalizeDateUTC(date), isCurrentMonth: false });
    }

    for (let day = 1; day <= lastDayOfMonth.getDate(); day++) {
      days.push({ date: normalizeDateUTC(new Date(year, month, day)), isCurrentMonth: true });
    }

    const remainingCells = (7 - (days.length % 7)) % 7;
    for (let i = 1; i <= remainingCells; i++) {
      const date = new Date(lastDayOfMonth);
      date.setDate(date.getDate() + i);
      days.push({ date: normalizeDateUTC(date), isCurrentMonth: false });
    }
    return days;
  };

  const daysForAgenda = getDaysInMonthForAgenda();
  const todayNormalized = normalizeDateUTC(new Date());
  const showGlobalSearchResults = viewMode === 'agenda' && hasSearchTerm;

  useEffect(() => {
    setSelectedAgendaDate(prev => normalizeDateUTC(prev));
  }, []);


  if (showFormOrEditingEntry !== null) {
    return (
      <div className="space-y-3">
        <button
          onClick={handleCloseForm} // ✅ Utiliser la nouvelle fonction
          className="flex items-center text-sm text-blue-600 hover:text-blue-800 mb-2"
        >
          <ArrowLeft size={16} className="mr-1" /> Retour au journal
        </button>
        <JournalEntryForm
          initialData={typeof showFormOrEditingEntry === 'object' ? showFormOrEditingEntry : null}
          onSubmit={handleFormSubmit}
          onCancel={handleCloseForm} // ✅ Utiliser la nouvelle fonction
          projectId={projectId}
          project={project}
          employees={employees}
          isSaving={isSaving}
        />
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex justify-between items-center">
        <h3 className="text-base font-semibold text-gray-800">Journal du Projet</h3>
        <div className="flex space-x-1">
          <button
            onClick={() => setViewMode('list')}
            title="Vue Liste"
            className={`p-1.5 rounded-md ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}
          > <List size={16} /> </button>
          <button
            onClick={() => setViewMode('agenda')}
            title="Vue Agenda"
            className={`p-1.5 rounded-md ${viewMode === 'agenda' ? 'bg-blue-100 text-blue-600' : 'text-gray-500 hover:bg-gray-100'}`}
          > <CalendarIcon size={16} /> </button>
        </div>
      </div>

      <button
        onClick={() => handleAddNewEntryForDate(selectedAgendaDate)}
        className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-blue-600 bg-white hover:bg-blue-50 hover:border-blue-400 transition-colors shadow-sm"
      >
        <Plus size={16} className="mr-2" /> Nouvelle Entrée
      </button>

      <div className="mt-3">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 pointer-events-none" />
          <input
            type="text"
            placeholder="Rechercher dans tout le journal..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-blue-500 focus:border-blue-500 shadow-sm"
          />
        </div>
      </div>

      {showGlobalSearchResults && (
        <div className="mt-3 border border-gray-200 rounded-md p-3 bg-white shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div>
              <p className="text-xs font-medium text-gray-700">
                Resultats globaux ({filteredEntriesForList.length})
              </p>
              <p className="text-[11px] text-gray-500">
                Recherche : "{trimmedSearchTerm}"
              </p>
            </div>
            <button
              onClick={() => setViewMode('list')}
              className="text-xs text-blue-600 hover:text-blue-700 font-medium"
            >
              Ouvrir la vue liste
            </button>
          </div>
          <ul className="space-y-2 max-h-60 overflow-y-auto pr-1">
            {filteredEntriesForList.length > 0 ? (
              filteredEntriesForList.map(entry => (
                <JournalEntryListItem
                  key={`agenda-search-${entry.id}`}
                  entry={entry}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                  getUsernameById={getUsernameById}
                  isDeleting={deletingId}
                />
              ))
            ) : (
              <p className="text-xs text-gray-500 italic text-center py-2">
                Aucun resultat ne correspond a cette recherche.
              </p>
            )}
          </ul>
        </div>
      )}

      {viewMode === 'list' && (
        <ul
          className="mt-2 grid gap-3 grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 overflow-y-auto pr-1 list-none pl-0"
          style={{ maxHeight: 'calc(5 * 190px)' }}
        >
          {filteredEntriesForList.length > 0 ? (
            filteredEntriesForList.map(entry => (
              <JournalEntryListItem
                key={entry.id}
                entry={entry}
                onEdit={handleEdit}
                onDelete={handleDelete}
                getUsernameById={getUsernameById}
                isDeleting={deletingId}
              />
            ))
          ) : (
            <p className="text-xs text-gray-500 italic text-center py-3">Aucune entrée trouvée.</p>
          )}
        </ul>
      )}

      {viewMode === 'agenda' && (
        <div className="mt-2 journal-agenda-container">
          <div className="flex justify-between items-center mb-2">
            <button onClick={() => changeMonth(-1)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full">
              <ChevronLeft size={18} />
            </button>
            <h4 className="text-sm font-medium text-gray-700">
              {currentMonthDate.toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' })}
            </h4>
            <button onClick={() => changeMonth(1)} className="p-1.5 text-gray-600 hover:bg-gray-100 rounded-full">
              <ChevronRight size={18} />
            </button>
          </div>
          <div className="journal-agenda-grid">
            {daysOfWeek.map(day => (
              <div key={day} className="journal-agenda-header">{day}</div>
            ))}
            {daysForAgenda.map(({ date, isCurrentMonth }) => {
              const dateISO = date.toISOString();
              const isHovered = hoveredDay === dateISO;
              const entriesOnThisDay = journalEntries
                .filter(entry => {
                  const normalizedEntryDate = normalizeDateUTC(entry.entry_date);
                  return normalizedEntryDate.getTime() === date.getTime();
                })
                .filter(entryMatchesSearch);

              const normalizedSelectedAgendaDate = normalizeDateUTC(selectedAgendaDate);
              const isSelected = normalizedSelectedAgendaDate.getTime() === date.getTime();
              const isToday = date.getTime() === todayNormalized.getTime();

              return (
                <div
                  key={dateISO}
                  className={`journal-agenda-day-cell relative group ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''} ${isSelected ? 'selected-day' : ''}`}
                  onClick={() => setSelectedAgendaDate(new Date(date))}
                  onMouseEnter={() => setHoveredDay(dateISO)}
                  onMouseLeave={() => setHoveredDay(null)}
                >
                  <span className="journal-agenda-day-number">{date.getUTCDate()}</span>

                  <button
                    className={`absolute top-0.5 right-0.5 p-1 rounded-full text-blue-500 bg-white shadow hover:bg-blue-100 transition-opacity duration-150 ${isHovered ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}
                    onClick={(e) => { e.stopPropagation(); handleAddNewEntryForDate(new Date(date)); }}
                    title={`Ajouter une entrée pour le ${new Date(date).toLocaleDateString()}`}
                  >
                    <Plus size={14} />
                  </button>

                  <div className="mt-3 space-y-1">
                    {entriesOnThisDay.slice(0, 2).map(entry => (
                      <div
                        key={entry.id}
                        className="journal-agenda-entry-preview"
                        title={entry.title}
                        onClick={(e) => { e.stopPropagation(); handleEdit(entry); }}
                      >
                        {entry.title}
                      </div>
                    ))}
                    {entriesOnThisDay.length > 2 && (
                      <div className="text-[10px] text-gray-500 italic text-center">
                        +{entriesOnThisDay.length - 2} autres
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
          {selectedAgendaDate && (
            <div className="mt-4">
              <h4 className="text-sm font-medium mb-1.5 pt-1.5 border-t">
                Entrées pour le {new Date(selectedAgendaDate.getUTCFullYear(), selectedAgendaDate.getUTCMonth(), selectedAgendaDate.getUTCDate()).toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}:
              </h4>
              <ul className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {entriesForSelectedAgendaDate.length > 0 ? (
                  entriesForSelectedAgendaDate.map(entry => (
                    <JournalEntryListItem
                      key={entry.id}
                      entry={entry}
                      onEdit={handleEdit}
                      onDelete={handleDelete}
                      getUsernameById={getUsernameById}
                      isDeleting={deletingId}
                    />
                  ))
                ) : (
                  <p className="text-xs text-gray-500 italic text-center py-2">Aucune entrée pour cette date{searchTerm ? " avec les filtres actuels" : ""}.</p>
                )}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default JournalPanel;
